/**
 * Alshallal Pastry Company HR System - Admin Monitoring Dashboard
 * Phase 18.5.1: Pilot Rollout Enablement
 * 
 * Provides operational visibility for Super Admin during pilot rollout.
 * Displays system health, OCR metrics, and key indicators.
 * Super Admin only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Activity,
  Database,
  Shield,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Zap,
  FileText,
  TrendingUp,
  Server,
  Eye,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ============== Status Indicators ==============

const StatusBadge = ({ status }) => {
  const colors = {
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200'
  };
  
  const icons = {
    green: <CheckCircle2 className="h-3 w-3" />,
    yellow: <AlertTriangle className="h-3 w-3" />,
    red: <XCircle className="h-3 w-3" />
  };
  
  return (
    <Badge variant="outline" className={`${colors[status]} flex items-center gap-1`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

// ============== Metric Card ==============

const MetricCard = ({ title, value, subtitle, icon: Icon, status, trend }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            status === 'green' ? 'bg-green-100' :
            status === 'yellow' ? 'bg-yellow-100' :
            status === 'red' ? 'bg-red-100' : 'bg-slate-100'
          }`}>
            <Icon className={`h-5 w-5 ${
              status === 'green' ? 'text-green-600' :
              status === 'yellow' ? 'text-yellow-600' :
              status === 'red' ? 'text-red-600' : 'text-slate-600'
            }`} />
          </div>
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
        {trend && (
          <Badge variant="outline" className="text-xs">
            {trend}
          </Badge>
        )}
      </div>
    </CardContent>
  </Card>
);

// ============== Main Component ==============

const AdminMonitoringPage = () => {
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  
  const [diagnostics, setDiagnostics] = useState(null);
  const [ocrMetrics, setOcrMetrics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [ocrWindow, setOcrWindow] = useState('24h');
  
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [diagResponse, ocrResponse, statusResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/diagnostics`).catch(e => ({ data: null, error: e })),
        axios.get(`${API_URL}/admin/system/ocr-metrics?window=${ocrWindow}`).catch(e => ({ data: null, error: e })),
        axios.get(`${API_URL}/admin/system-status`).catch(e => ({ data: null, error: e }))
      ]);
      
      if (diagResponse.data) setDiagnostics(diagResponse.data);
      if (ocrResponse.data) setOcrMetrics(ocrResponse.data);
      if (statusResponse.data) setSystemStatus(statusResponse.data);
      
      setLastUpdated(new Date());
    } catch (error) {
      toast.error(isRTL ? 'فشل في جلب بيانات المراقبة' : 'Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  }, [ocrWindow, isRTL]);
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  // Refresh when OCR window changes
  useEffect(() => {
    if (!loading) {
      axios.get(`${API_URL}/admin/system/ocr-metrics?window=${ocrWindow}`)
        .then(res => setOcrMetrics(res.data))
        .catch(() => {});
    }
  }, [ocrWindow, loading]);
  
  // Check if user is super admin
  if (!user || user.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="p-4 bg-red-100 rounded-full w-fit mx-auto mb-6">
              <Shield className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              {isRTL ? 'غير مصرح' : 'Access Denied'}
            </h1>
            <p className="text-slate-600">
              {isRTL 
                ? 'هذه الصفحة متاحة فقط لمديري النظام (Super Admin)'
                : 'This page is only accessible to Super Admins'
              }
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Calculate overall system health
  const getOverallHealth = () => {
    if (!diagnostics?.services) return 'yellow';
    const statuses = Object.values(diagnostics.services).map(s => s.status);
    if (statuses.includes('red')) return 'red';
    if (statuses.includes('yellow')) return 'yellow';
    return 'green';
  };
  
  const overallHealth = getOverallHealth();
  
  return (
    <div className="space-y-6" data-testid="admin-monitoring-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'لوحة المراقبة' : 'Monitoring Dashboard'}
          </h1>
          <p className="text-slate-500">
            {isRTL ? 'مراقبة النظام للتجربة التشغيلية' : 'System monitoring for pilot operations'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-500">
            <Clock className="h-4 w-4 inline mr-1" />
            {isRTL ? 'آخر تحديث: ' : 'Last updated: '}
            {lastUpdated ? lastUpdated.toLocaleTimeString() : '-'}
          </div>
          <Button variant="outline" onClick={fetchAllData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
        </div>
      </div>
      
      {/* Overall Health Banner */}
      <Card className={`border-2 ${
        overallHealth === 'green' ? 'border-green-200 bg-green-50' :
        overallHealth === 'yellow' ? 'border-yellow-200 bg-yellow-50' :
        'border-red-200 bg-red-50'
      }`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                overallHealth === 'green' ? 'bg-green-200' :
                overallHealth === 'yellow' ? 'bg-yellow-200' : 'bg-red-200'
              }`}>
                {overallHealth === 'green' ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : overallHealth === 'yellow' ? (
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
              </div>
              <div>
                <p className="font-semibold text-lg">
                  {overallHealth === 'green' 
                    ? (isRTL ? 'جميع الأنظمة تعمل بشكل طبيعي' : 'All Systems Operational')
                    : overallHealth === 'yellow'
                    ? (isRTL ? 'بعض الأنظمة تحتاج إلى انتباه' : 'Some Systems Need Attention')
                    : (isRTL ? 'مشاكل حرجة تحتاج إصلاح' : 'Critical Issues Detected')
                  }
                </p>
                <p className="text-sm text-slate-600">
                  {diagnostics?.environment || 'production'} - {diagnostics?.instance?.customer_name || (isRTL ? 'معجنات الشلال' : 'Alshallal Pastry')}
                </p>
              </div>
            </div>
            <StatusBadge status={overallHealth} />
          </div>
        </CardContent>
      </Card>
      
      {/* Service Health Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-3">
          {isRTL ? 'صحة الخدمات' : 'Service Health'}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {diagnostics?.services && Object.entries(diagnostics.services).map(([key, service]) => (
            <Card key={key} className={`${
              service.status === 'green' ? 'border-green-200' :
              service.status === 'yellow' ? 'border-yellow-200' : 'border-red-200'
            }`}>
              <CardContent className="p-3 text-center">
                <div className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                  service.status === 'green' ? 'bg-green-100' :
                  service.status === 'yellow' ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  {service.status === 'green' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : service.status === 'yellow' ? (
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                </div>
                <p className="text-xs font-medium capitalize">
                  {key.replace('_', ' ')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      
      {/* OCR Metrics Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">
            {isRTL ? 'مقاييس OCR' : 'OCR Metrics'}
          </h2>
          <Select value={ocrWindow} onValueChange={setOcrWindow}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">{isRTL ? 'ساعة واحدة' : '1 Hour'}</SelectItem>
              <SelectItem value="24h">{isRTL ? '24 ساعة' : '24 Hours'}</SelectItem>
              <SelectItem value="7d">{isRTL ? '7 أيام' : '7 Days'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {ocrMetrics ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <MetricCard
              title={isRTL ? 'إجمالي المهام' : 'Total Jobs'}
              value={ocrMetrics.jobs_total || 0}
              icon={FileText}
              status="green"
            />
            <MetricCard
              title={isRTL ? 'مكتمل' : 'Completed'}
              value={ocrMetrics.jobs_completed || 0}
              icon={CheckCircle2}
              status="green"
            />
            <MetricCard
              title={isRTL ? 'فشل' : 'Failed'}
              value={ocrMetrics.jobs_failed || 0}
              icon={XCircle}
              status={ocrMetrics.jobs_failed > 0 ? 'red' : 'green'}
            />
            <MetricCard
              title={isRTL ? 'معدل الخطأ' : 'Error Rate'}
              value={`${((ocrMetrics.error_rate || 0) * 100).toFixed(1)}%`}
              icon={TrendingUp}
              status={(ocrMetrics.error_rate || 0) > 0.05 ? 'yellow' : 'green'}
            />
            <MetricCard
              title={isRTL ? 'زمن الاستجابة (p95)' : 'Latency (p95)'}
              value={`${ocrMetrics.p95_total_ms || 0}ms`}
              icon={Zap}
              status={(ocrMetrics.p95_total_ms || 0) > 500 ? 'yellow' : 'green'}
            />
            <MetricCard
              title={isRTL ? 'عمق الطابور' : 'Queue Depth'}
              value={ocrMetrics.queue_depth_current || 0}
              icon={BarChart3}
              status={(ocrMetrics.queue_depth_current || 0) > 50 ? 'yellow' : 'green'}
            />
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              <Activity className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              {isRTL ? 'لا توجد بيانات OCR متاحة' : 'No OCR metrics available'}
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Database Stats */}
      <div>
        <h2 className="text-lg font-semibold mb-3">
          {isRTL ? 'إحصائيات قاعدة البيانات' : 'Database Statistics'}
        </h2>
        {systemStatus?.database?.collections ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(systemStatus.database.collections).map(([name, count]) => (
              <MetricCard
                key={name}
                title={name.replace('_', ' ')}
                value={count.toLocaleString()}
                icon={Database}
                status="green"
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              <Database className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              {isRTL ? 'لا توجد بيانات متاحة' : 'No data available'}
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Storage Stats */}
      {systemStatus?.storage && (
        <div>
          <h2 className="text-lg font-semibold mb-3">
            {isRTL ? 'التخزين' : 'Storage'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title={isRTL ? 'الحالة' : 'Status'}
              value={systemStatus.storage.status === 'available' 
                ? (isRTL ? 'متاح' : 'Available') 
                : (isRTL ? 'غير متاح' : 'Unavailable')}
              icon={Server}
              status={systemStatus.storage.status === 'available' ? 'green' : 'red'}
            />
            <MetricCard
              title={isRTL ? 'عدد الملفات' : 'File Count'}
              value={(systemStatus.storage.file_count || 0).toLocaleString()}
              icon={FileText}
              status="green"
            />
            <MetricCard
              title={isRTL ? 'الحجم الكلي' : 'Total Size'}
              value={`${systemStatus.storage.total_size_mb || 0} MB`}
              icon={Database}
              status="green"
            />
            <MetricCard
              title={isRTL ? 'قابل للكتابة' : 'Writable'}
              value={systemStatus.storage.writable ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No')}
              icon={Eye}
              status={systemStatus.storage.writable ? 'green' : 'red'}
            />
          </div>
        </div>
      )}
      
      {/* Warnings */}
      {diagnostics?.warnings?.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              {isRTL ? 'تحذيرات' : 'Warnings'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
              {diagnostics.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminMonitoringPage;
